import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api/client';
import { Section, SectionEyebrow, SectionTitle } from '../components/ui/Section';
import { PillButton } from '../components/ui/PillButton';
import { TextInput, TextArea } from '../components/ui/TextInput';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Chip } from '../components/ui/Chip';
import { useAuth } from '../lib/auth/store';
import { formatDate, formatTime } from '../lib/format';
import { useDialog } from '../lib/dialog/hooks';
import { JOURNAL_TYPES } from '@mindrift/shared';
import type { JournalType, ProposedTask } from '@mindrift/shared';

export const Route = createFileRoute('/_app/journal')({
  component: JournalPage,
});

function JournalPage() {
  const settings = useAuth((s) => s.settings);
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [areaFilter, setAreaFilter] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['journal', 'list', areaFilter],
    queryFn: () => api.journal.list({ limit: 100, area: areaFilter ?? undefined }),
  });

  const areas = ['Work', 'Personal', 'Health', 'Social', ...(settings?.customAreas ?? [])];
  const entries = data?.entries ?? [];
  const active = selectedId ? entries.find((e) => e.id === selectedId) : null;

  return (
    <>
      <Section surface="canvas">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <SectionEyebrow>JOURNAL</SectionEyebrow>
            <SectionTitle size="section-heading" className="mt-3">A page for what you're thinking.</SectionTitle>
          </div>
          <PillButton onClick={() => setShowForm(true)}>New entry</PillButton>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <aside className="lg:col-span-1">
            <div className="flex flex-wrap gap-1.5 mb-4">
              {areas.map((a) => (
                <button
                  key={a}
                  onClick={() => setAreaFilter(areaFilter === a ? null : a)}
                  className={`pill-outline text-micro ${areaFilter === a ? 'bg-near-black text-canvas-white border-near-black' : ''}`}
                  type="button"
                >
                  {a}
                </button>
              ))}
            </div>
            {isLoading ? (
              <p className="text-muted-slate">Loading…</p>
            ) : entries.length === 0 ? (
              <EmptyState
                title="No entries yet."
                description="Capture a thought, a frustration, or a small win."
                action={<PillButton onClick={() => setShowForm(true)}>Write one</PillButton>}
              />
            ) : (
              <ul>
                {entries.map((e) => (
                  <li key={e.id} className="border-b border-hairline">
                    <button
                      onClick={() => setSelectedId(e.id)}
                      className={`w-full text-left py-4 ${selectedId === e.id ? 'pl-3 border-l-2 border-near-black' : ''}`}
                    >
                      <p className="field-label">{e.type.toUpperCase()}</p>
                      <p className="mt-1 text-body text-ink line-clamp-2">{e.title || e.body.slice(0, 80)}</p>
                      <p className="text-caption text-muted-slate mt-1">{formatDate(e.createdAt)} · {formatTime(e.createdAt)}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <div className="lg:col-span-2">
            {showForm ? (
              <NewEntryForm
                areas={['Personal', ...(settings?.customAreas ?? [])]}
                onClose={() => setShowForm(false)}
                onSaved={(id) => {
                  setSelectedId(id);
                  setShowForm(false);
                  qc.invalidateQueries({ queryKey: ['journal'] });
                }}
              />
            ) : active ? (
              <EntryDetail
                entry={active}
                areas={['Personal', ...(settings?.customAreas ?? [])]}
                onUpdate={() => qc.invalidateQueries({ queryKey: ['journal'] })}
                onDelete={() => {
                  setSelectedId(null);
                  qc.invalidateQueries({ queryKey: ['journal'] });
                }}
              />
            ) : (
              <div className="rounded-md border border-dashed border-hairline p-12 text-center">
                <p className="text-body text-muted-slate">Pick an entry to read or edit.</p>
              </div>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}

function NewEntryForm({ areas, onClose, onSaved }: { areas: string[]; onClose: () => void; onSaved: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState<JournalType>('Journal');
  const [area, setArea] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.journal.create({ title: title.trim() || null, body: body.trim(), type, area }),
    onSuccess: (res) => onSaved(res.entry.id),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  return (
    <Card>
      <div className="flex flex-wrap gap-2 mb-4">
        {JOURNAL_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            className={`pill-outline text-caption ${type === t ? 'bg-near-black text-canvas-white border-near-black' : ''}`}
            type="button"
          >
            {t}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-4">
        <TextInput label="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextArea
          label="What's on your mind?"
          rows={10}
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <label className="field-label">Area (optional)</label>
          <select
            className="rounded-md border border-hairline px-3 py-3 text-body bg-canvas-white"
            value={area ?? ''}
            onChange={(e) => setArea(e.target.value || null)}
          >
            <option value="">—</option>
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {error && <p className="text-caption text-error-red">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <PillButton variant="ghost" onClick={onClose} type="button">Cancel</PillButton>
          <PillButton onClick={() => create.mutate()} loading={create.isPending} disabled={!body.trim()}>Save</PillButton>
        </div>
      </div>
    </Card>
  );
}

function EntryDetail({
  entry,
  areas,
  onUpdate,
  onDelete,
}: {
  entry: any;
  areas: string[];
  onUpdate: () => void;
  onDelete: () => void;
}) {
  const [tidyResult, setTidyResult] = useState<string | null>(null);
  const [convertTasks, setConvertTasks] = useState<ProposedTask[] | null>(null);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [working, setWorking] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const qc = useQueryClient();
  const dialog = useDialog();

  const tidy = useMutation({
    mutationFn: () => api.journal.aiTidy({ body: entry.body, type: entry.type }),
    onSuccess: (res) => setTidyResult(res.cleaned),
  });

  const convert = useMutation({
    mutationFn: () => api.journal.aiConvert({ body: entry.body, type: entry.type, area: entry.area }),
    onSuccess: (res) => {
      setConvertTasks(res.tasks);
      setSelected(Object.fromEntries(res.tasks.map((_, i) => [i, true])));
    },
  });

  const acceptTidy = useMutation({
    mutationFn: () => api.journal.update(entry.id, { body: tidyResult! }),
    onSuccess: () => {
      setTidyResult(null);
      onUpdate();
    },
  });

  const commitConvert = async () => {
    if (!convertTasks) return;
    setWorking(true);
    try {
      const picked = convertTasks.filter((_, i) => selected[i]);
      if (picked.length > 0) {
        await api.journal.aiConvertCommit(picked);
        qc.invalidateQueries({ queryKey: ['tasks'] });
      }
      setConvertTasks(null);
      setSelected({});
    } finally {
      setWorking(false);
    }
  };

  const remove = useMutation({
    mutationFn: () => api.journal.remove(entry.id),
    onSuccess: onDelete,
  });

  if (isEditing) {
    return (
      <EntryEditForm
        entry={entry}
        areas={areas}
        saving={false}
        error={editError}
        onSave={async (payload) => {
          setEditError(null);
          try {
            await api.journal.update(entry.id, payload).then(() => onUpdate());
            setIsEditing(false);
          } catch (e) {
            setEditError(e instanceof ApiError ? e.message : 'Failed to save');
          }
        }}
        onCancel={() => {
          setEditError(null);
          setIsEditing(false);
        }}
      />
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <p className="field-label">{entry.type.toUpperCase()} · {formatDate(entry.createdAt)}</p>
        {entry.area && <Chip>{entry.area}</Chip>}
      </div>
      {entry.title && <h2 className="text-feature-heading font-display text-ink mt-2 mb-4">{entry.title}</h2>}
      <p className="text-body-lg text-ink whitespace-pre-wrap leading-relaxed">{entry.body}</p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <PillButton variant="outline" onClick={() => setIsEditing(true)}>Edit</PillButton>
        <PillButton variant="outline" onClick={() => tidy.mutate()} loading={tidy.isPending}>
          Tidy with AI
        </PillButton>
        <PillButton variant="outline" onClick={() => convert.mutate()} loading={convert.isPending}>
          Convert to tasks
        </PillButton>
        <div className="flex-1" />
        <button
          onClick={async () => {
            const ok = await dialog.confirm(
              'Delete this entry?',
              'This will permanently remove the journal entry.',
              { tone: 'danger', confirmLabel: 'Delete' },
            );
            if (ok) remove.mutate();
          }}
          className="text-caption text-muted-slate hover:text-error-red"
        >
          Delete
        </button>
      </div>

      {tidyResult && (
        <div className="mt-8 border-t border-hairline pt-6">
          <p className="field-label">AI TIDY · PREVIEW</p>
          <p className="mt-3 text-body text-ink whitespace-pre-wrap leading-relaxed bg-pale-green-wash rounded-md p-4">
            {tidyResult}
          </p>
          <div className="mt-3 flex gap-2">
            <PillButton onClick={() => acceptTidy.mutate()} loading={acceptTidy.isPending}>Accept</PillButton>
            <PillButton variant="ghost" onClick={() => setTidyResult(null)}>Discard</PillButton>
          </div>
        </div>
      )}

      {convertTasks && (
        <div className="mt-8 border-t border-hairline pt-6">
          <p className="field-label">AI SUGGESTED TASKS</p>
          <ul className="mt-3 flex flex-col gap-2">
            {convertTasks.map((t, i) => (
              <li key={i} className="flex items-start gap-3 p-3 rounded-md border border-hairline">
                <input
                  type="checkbox"
                  checked={!!selected[i]}
                  onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                  className="mt-1"
                />
                <div>
                  <p className="text-body text-ink">{t.title}</p>
                  {t.description && <p className="text-caption text-muted-slate mt-1">{t.description}</p>}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex gap-2">
            <PillButton onClick={commitConvert} loading={working} disabled={Object.values(selected).every((v) => !v)}>
              Add to todos
            </PillButton>
            <PillButton variant="ghost" onClick={() => setConvertTasks(null)}>Cancel</PillButton>
          </div>
        </div>
      )}
    </Card>
  );
}

function EntryEditForm({
  entry,
  areas,
  saving,
  error,
  onSave,
  onCancel,
}: {
  entry: any;
  areas: string[];
  saving: boolean;
  error: string | null;
  onSave: (payload: { title: string | null; body: string; type: JournalType; area: string | null }) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(entry.title ?? '');
  const [body, setBody] = useState(entry.body);
  const [type, setType] = useState<JournalType>(entry.type);
  const [area, setArea] = useState<string | null>(entry.area ?? null);
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await onSave({ title: title.trim() || null, body: body.trim(), type, area });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap gap-2 mb-4">
        {JOURNAL_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            className={`pill-outline text-caption ${type === t ? 'bg-near-black text-canvas-white border-near-black' : ''}`}
            type="button"
          >
            {t}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-4">
        <TextInput
          label="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <TextArea
          label="Body"
          rows={12}
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <label className="field-label">Area (optional)</label>
          <select
            className="rounded-md border border-hairline px-3 py-3 text-body bg-canvas-white"
            value={area ?? ''}
            onChange={(e) => setArea(e.target.value || null)}
          >
            <option value="">—</option>
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {error && <p className="text-caption text-error-red">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <PillButton variant="ghost" onClick={onCancel} type="button">Cancel</PillButton>
          <PillButton onClick={handleSave} loading={busy || saving} disabled={!body.trim()}>Save changes</PillButton>
        </div>
      </div>
    </Card>
  );
}
